import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import { ApiResponse } from '../types/api';
import {
  PlanCatalogItem,
  SubscriptionDetails,
  SubscribeData,
  SubscribeResult,
  ConfirmSubscriptionData,
  ChangePlanData,
  ChangeSeatsData,
  CancelResult,
  Payment,
  UsageData,
  ReconciliationState,
  ReconciliationChoices,
} from '../types/billing';

// All billing queries live under the ['billing', …] key namespace; every
// mutation invalidates ['billing'] so the subscription/usage/payments views
// refetch together.
const BILLING_KEY = ['billing'] as const;

// ─── Catalog ──────────────────────────────────────────────────────────────

/**
 * List the available plans (pricing, seats, trials).
 * GET /api/v1/billing/catalog
 */
export const fetchCatalog = async (): Promise<PlanCatalogItem[]> => {
  const response = await api.get<ApiResponse<{ plans: PlanCatalogItem[] }>>('/billing/catalog');
  return response.data.data?.plans ?? [];
};

export const useCatalog = (): UseQueryResult<PlanCatalogItem[], Error> =>
  useQuery({
    queryKey: ['billing', 'catalog'],
    queryFn: fetchCatalog,
    staleTime: 30 * 60 * 1000,
  });

// ─── Usage ──────────────────────────────────────────────────────────────

/**
 * Current seat + course usage for the active tenant.
 * GET /api/v1/billing/usage
 */
export const fetchUsage = async (): Promise<UsageData> => {
  const response = await api.get<ApiResponse<UsageData>>('/billing/usage');
  return response.data.data!;
};

export const useUsage = (): UseQueryResult<UsageData, Error> =>
  useQuery({
    queryKey: ['billing', 'usage'],
    queryFn: fetchUsage,
    staleTime: 60 * 1000,
  });

// ─── Subscription ──────────────────────────────────────────────────────────

/**
 * Get the current subscription. Defaults to the free plan when none exists.
 * GET /api/v1/billing/subscription
 */
export const fetchSubscription = async (): Promise<SubscriptionDetails> => {
  const response = await api.get<ApiResponse<SubscriptionDetails>>('/billing/subscription');
  return response.data.data!;
};

export const useSubscription = (): UseQueryResult<SubscriptionDetails, Error> =>
  useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: fetchSubscription,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Start a subscription. Returns a Revolut order token that the embedded
 * checkout widget consumes; the webhook is the authoritative backstop.
 * POST /api/v1/billing/subscribe
 */
export const subscribe = async (data: SubscribeData): Promise<SubscribeResult> => {
  const response = await api.post<ApiResponse<SubscribeResult>>('/billing/subscribe', data);
  return response.data.data!;
};

export const useSubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: subscribe,
    // Settled, not success: a 409 ALREADY_SUBSCRIBED can mean the server just
    // found an earlier checkout was paid after all and promoted it — the
    // screen must refetch to show that plan.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    },
  });
};

/**
 * Confirm a subscription after the card field reports success.
 * POST /api/v1/billing/subscription/confirm
 */
export const confirmSubscription = async (
  data: ConfirmSubscriptionData
): Promise<SubscriptionDetails> => {
  const response = await api.post<ApiResponse<SubscriptionDetails>>(
    '/billing/subscription/confirm',
    data
  );
  return response.data.data!;
};

export const useConfirmSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    },
  });
};

/**
 * Change the subscription plan.
 * PUT /api/v1/billing/plan
 */
export const changePlan = async (data: ChangePlanData): Promise<SubscriptionDetails> => {
  const response = await api.put<ApiResponse<SubscriptionDetails>>('/billing/plan', data);
  return response.data.data!;
};

export const useChangePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    },
  });
};

/**
 * Change the number of purchased seats.
 * PUT /api/v1/billing/seats
 */
export const changeSeats = async (data: ChangeSeatsData): Promise<SubscriptionDetails> => {
  const response = await api.put<ApiResponse<SubscriptionDetails>>('/billing/seats', data);
  return response.data.data!;
};

export const useChangeSeats = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changeSeats,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    },
  });
};

/**
 * Cancel at period end.
 * POST /api/v1/billing/cancel
 */
export const cancelSubscription = async (): Promise<CancelResult> => {
  const response = await api.post<ApiResponse<CancelResult>>('/billing/cancel');
  return response.data.data!;
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    },
  });
};

// ─── Payments ──────────────────────────────────────────────────────────────

/**
 * List recent payments (amounts in minor units).
 * GET /api/v1/billing/payments?limit=
 */
export const fetchPayments = async (limit = 20): Promise<Payment[]> => {
  const response = await api.get<ApiResponse<{ payments: Payment[] }>>('/billing/payments', {
    params: { limit },
  });
  return response.data.data?.payments ?? [];
};

export const usePayments = (limit = 20): UseQueryResult<Payment[], Error> =>
  useQuery({
    queryKey: ['billing', 'payments', limit],
    queryFn: () => fetchPayments(limit),
    staleTime: 5 * 60 * 1000,
  });

// ─── Downgrade reconciliation ─────────────────────────────────────────────

/**
 * The choose-what-stays state: caps, overages, deadline, and (while open)
 * the pickable inventory. GET /api/v1/billing/reconciliation
 */
export const fetchReconciliation = async (): Promise<ReconciliationState> => {
  const response = await api.get<ApiResponse<ReconciliationState>>('/billing/reconciliation');
  return response.data.data as ReconciliationState;
};

export const useReconciliation = (
  enabled = true
): UseQueryResult<ReconciliationState, Error> =>
  useQuery({
    queryKey: [...BILLING_KEY, 'reconciliation'],
    queryFn: fetchReconciliation,
    enabled,
  });

/** Apply the admin's keep-lists. POST /api/v1/billing/reconciliation */
export const applyReconciliation = async (
  choices: ReconciliationChoices
): Promise<ReconciliationState> => {
  const response = await api.post<ApiResponse<ReconciliationState>>(
    '/billing/reconciliation',
    choices
  );
  return response.data.data as ReconciliationState;
};

export const useApplyReconciliation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: applyReconciliation,
    onSuccess: () => {
      // The choice reshapes members, courses, and programs everywhere.
      queryClient.invalidateQueries();
    },
  });
};
